import React, { useState, useEffect, useCallback, useRef } from 'react';
import Button from '../../components/Button';
import {
  IconX, IconPencil, IconPlus, IconSearch, IconTrash, IconRefresh,
  IconChevronRight, IconChevronDown,
} from '@tabler/icons-react';
import api from '../../services/api';

// ---------------------------------------------------------------------------
// Form shape
//
//   form = {
//     generic_name, rxnorm_cui_ingredient, highlight_in_list, is_active,
//     forms:   [ { display_label, strength_form, rxnorm_cui, include, sort_order } ],  // parent SCD
//     brands:  [ { brand_name, bn_cui, sort_order,
//                  forms: [ { display_label, strength_form, rxnorm_cui, include, sort_order } ] } ], // SBD
//     classes: [ { class_id, class_name, source, sort_order } ],
//   }
//
// `include` is the per-form inclusion gate (checked = saved). It is the UI
// mirror of is_common on the backend; on save, only included forms are sent,
// each with is_common=true.
// ---------------------------------------------------------------------------

const EMPTY_ENTRY = {
  generic_name: '',
  rxnorm_cui_ingredient: '',
  highlight_in_list: false,
  is_active: true,
  forms: [],
  brands: [],
  classes: [],
};

// Map a saved form (CatalogForm) into the modal's form-row shape.
function formToRow(f, i, included) {
  return {
    display_label: f.display_label || '',
    strength_form: f.strength_form || '',
    rxnorm_cui: f.rxnorm_cui || '',
    include: included,
    sort_order: f.sort_order ?? i,
  };
}

// Map a CatalogFormIn (resolve/hydrate draft) into a form-row, unchecked.
function draftFormToRow(f, i) {
  return {
    display_label: f.display_label || '',
    strength_form: f.strength_form || '',
    rxnorm_cui: f.rxnorm_cui || '',
    include: false,   // resolved candidates start unchecked; curator selects
    sort_order: f.sort_order ?? i,
  };
}

// Saved entry (CatalogAdminOut) -> modal form. Saved forms are included (checked).
function entryToForm(e) {
  return {
    generic_name: e.generic_name || '',
    rxnorm_cui_ingredient: e.rxnorm_cui_ingredient || '',
    highlight_in_list: e.highlight_in_list || false,
    is_active: e.is_active ?? true,
    forms: (e.forms || []).map((f, i) => formToRow(f, i, true)),
    brands: (e.brands || []).map((b, bi) => ({
      brand_name: b.brand_name || '',
      bn_cui: b.bn_cui || '',
      sort_order: b.sort_order ?? bi,
      forms: (b.forms || []).map((f, i) => formToRow(f, i, true)),
    })),
    classes: (e.classes || []).map((c, i) => ({
      class_id: c.class_id || '',
      class_name: c.class_name || '',
      source: c.source || 'epc',
      sort_order: c.sort_order ?? i,
    })),
  };
}

// Resolve draft (CatalogResolveOut) -> modal form. All forms unchecked.
function resolveToForm(draft) {
  return {
    generic_name: draft.generic_name || '',
    rxnorm_cui_ingredient: draft.rxnorm_cui_ingredient || '',
    highlight_in_list: draft.suggested_highlight || false,
    is_active: true,
    forms: (draft.forms || []).map(draftFormToRow),
    brands: (draft.brands || []).map((b, bi) => ({
      brand_name: b.brand_name || '',
      bn_cui: b.bn_cui || '',
      sort_order: bi,
      forms: (b.forms || []).map(draftFormToRow),
    })),
    classes: (draft.classes || []).map((c, i) => ({
      class_id: c.class_id || '',
      class_name: c.class_name || '',
      source: c.source || 'epc',
      sort_order: c.sort_order ?? i,
    })),
  };
}

function formToPayload(form) {
  const mapForms = (rows) => rows
    .filter(f => f.include && f.display_label.trim())
    .map((f, idx) => ({
      display_label: f.display_label.trim(),
      strength_form: f.strength_form.trim() || null,
      rxnorm_cui: f.rxnorm_cui.trim() || null,
      is_common: true,   // every saved strength is a deliberate inclusion
      sort_order: parseInt(f.sort_order, 10) || idx,
    }));

  return {
    generic_name: form.generic_name.trim(),
    rxnorm_cui_ingredient: form.rxnorm_cui_ingredient.trim() || null,
    highlight_in_list: form.highlight_in_list,
    is_active: form.is_active,
    forms: mapForms(form.forms),
    brands: form.brands
      .filter(b => b.brand_name.trim())
      .map((b, bidx) => ({
        brand_name: b.brand_name.trim(),
        bn_cui: (b.bn_cui || '').trim() || null,
        sort_order: parseInt(b.sort_order, 10) || bidx,
        forms: mapForms(b.forms),
      })),
    classes: form.classes
      .filter(c => c.class_id && c.class_name)
      .map((c, idx) => ({
        class_id: c.class_id,
        class_name: c.class_name,
        source: c.source || 'epc',
        sort_order: c.sort_order ?? idx,
      })),
  };
}

// ===========================================================================
// NameSearch — Add-Entry entry state. Prefix type-ahead against the cached
// RxNorm display-name index. Picking a name triggers resolve in the parent.
// ===========================================================================

function NameSearch({ onPick, onManual }) {
  const [query, setQuery] = useState('');
  const [names, setNames] = useState([]);
  const [searching, setSearching] = useState(false);
  const timer = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  const run = useCallback(async (q) => {
    const term = q.trim();
    if (term.length < 3) { setNames([]); return; }
    setSearching(true);
    try {
      const res = await api.get(
        `/reference/medications/rxnorm/names?q=${encodeURIComponent(term)}`
      );
      setNames(res.data || []);
    } catch {
      setNames([]);
    } finally {
      setSearching(false);
    }
  }, []);

  const onChange = (v) => {
    setQuery(v);
    if (timer.current) clearTimeout(timer.current);
    if (v.trim().length < 3) { setNames([]); return; }
    timer.current = setTimeout(() => run(v), 300);
  };

  const ready = query.trim().length >= 3;

  return (
    <div style={ns.wrap}>
      <div style={ns.searchBar}>
        <IconSearch size={16} stroke={1.5} style={{ color: 'var(--color-text-muted)', flexShrink: 0 }} />
        <input
          ref={inputRef}
          style={ns.input}
          value={query}
          onChange={e => onChange(e.target.value)}
          placeholder="Search drug or brand name (e.g. metoprolol succinate, Jardiance)…"
          autoComplete="off"
        />
        {searching && <span style={ns.searchingText}>Searching…</span>}
      </div>

      <div style={ns.results}>
        {!ready && (
          <div style={ns.hint}>
            Type at least 3 characters. Pick a drug or brand to auto-fill the
            entry from RxNorm — generic name, strengths, classes, and brands.
          </div>
        )}
        {ready && names.length === 0 && !searching && (
          <div style={ns.hint}>No matches for &ldquo;{query.trim()}&rdquo;.</div>
        )}
        {names.map(name => (
          <div key={name} style={ns.row} onMouseDown={() => onPick(name)}>
            <span style={ns.rowText}>{name}</span>
            <IconChevronRight size={15} stroke={1.5}
              style={{ color: 'var(--color-text-muted)', flexShrink: 0 }} />
          </div>
        ))}
      </div>

      <div style={ns.manualRow}>
        <button style={ns.manualLink} onClick={onManual}>
          Drug not found? Enter manually instead.
        </button>
      </div>
    </div>
  );
}

// ===========================================================================
// FormRowList — the strength/form checklist for one owner (parent or brand).
// Pure presentational: receives rows + handlers, renders the include-checkbox
// list plus a manual-add row. Used identically in the left column (parent SCD)
// and inside each BrandPane (brand SBD).
// ===========================================================================

function FormRowList({ rows, onToggle, onEdit, onRemove, onAddManual, emptyHint }) {
  return (
    <>
      {rows.length === 0 && (
        <div style={m.hint}>{emptyHint}</div>
      )}
      {rows.map((f, idx) => (
        <div key={idx} style={m.formRow}>
          <label style={{ ...m.checkRow, flexShrink: 0 }} title="Include this strength in the catalog">
            <input type="checkbox" checked={f.include}
              onChange={e => onToggle(idx, e.target.checked)} />
          </label>
          <input style={{ ...m.input, flex: 2 }} value={f.display_label}
            onChange={e => onEdit(idx, 'display_label', e.target.value)}
            placeholder="Display label (e.g. 10 mg tablet)" />
          <input style={{ ...m.input, flex: 0.4 }} value={f.rxnorm_cui}
            onChange={e => onEdit(idx, 'rxnorm_cui', e.target.value)}
            placeholder="CUI" />
          <button style={m.removeBtn} onClick={() => onRemove(idx)} aria-label="Remove form">
            <IconTrash size={14} stroke={1.5} />
          </button>
        </div>
      ))}
      <button style={m.linkBtn} onClick={onAddManual}>+ add a strength manually</button>
    </>
  );
}

// ===========================================================================
// BrandPane — one hydrated brand in the right column. Self-contained: brand
// name, BN CUI, and its SBD strength checklist. All edits flow up via the
// onChange(updatedBrand) callback so the parent owns the canonical brands[].
// ===========================================================================

function BrandPane({ brand, onChange, onRemove }) {
  const setForms = (rows) => onChange({ ...brand, forms: rows });

  const toggle = (idx, checked) => {
    const rows = brand.forms.map((f, i) => i === idx ? { ...f, include: checked } : f);
    setForms(rows);
  };
  const editForm = (idx, key, value) => {
    const rows = brand.forms.map((f, i) => i === idx ? { ...f, [key]: value } : f);
    setForms(rows);
  };
  const removeForm = (idx) => setForms(brand.forms.filter((_, i) => i !== idx));
  const addManual = () => setForms([
    ...brand.forms,
    { display_label: '', strength_form: '', rxnorm_cui: '', include: true, sort_order: brand.forms.length },
  ]);

  const includedCount = brand.forms.filter(f => f.include).length;

  return (
    <div style={m.brandPane}>
      <div style={m.brandPaneHead}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 }}>
          <span style={m.brandPaneName}>{brand.brand_name}</span>
          {brand.bn_cui
            ? <span style={m.brandPaneCui}>BN CUI {brand.bn_cui}</span>
            : <span style={m.brandPaneCui}>no BN CUI resolved</span>}
        </div>
        <button style={m.brandPaneRemove} onClick={onRemove}
          title={`Remove ${brand.brand_name}`} aria-label="Remove brand">
          <IconX size={14} stroke={2} />
        </button>
      </div>
      <div style={m.brandPaneSub}>
        {includedCount} of {brand.forms.length} strengths selected
      </div>
      <FormRowList
        rows={brand.forms}
        onToggle={toggle}
        onEdit={editForm}
        onRemove={removeForm}
        onAddManual={addManual}
        emptyHint="No strengths resolved for this brand. Add one manually if needed."
      />
    </div>
  );
}

// ===========================================================================
// Edit / Add modal — two-column nested editor
//
// Left column:  ingredient identity, classes, parent generic SCD forms.
// Right column: brand panes (each a BrandPane) + click-to-add suggestions.
//
// Modes:
//   - Add (entry === null): opens in NAME-SEARCH state. Picking a name resolves
//     and hydrates. "Enter manually" drops to a blank form.
//   - Edit (entry object): opens hydrated from the stored entry. Re-pull from
//     RxNorm is a deliberate action, never automatic.
// ===========================================================================

function CatalogEntryModal({ entry, onSaved, onClose }) {
  const isEdit = !!entry?.id;

  const [phase, setPhase] = useState(isEdit ? 'form' : 'search');
  const [form, setForm] = useState(entry ? entryToForm(entry) : EMPTY_ENTRY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Resolve state
  const [resolving, setResolving] = useState(false);
  const [resolveError, setResolveError] = useState('');
  const [pickedName, setPickedName] = useState('');
  const [wasBrandPick, setWasBrandPick] = useState(false);
  const [brandSuggestions, setBrandSuggestions] = useState([]); // names
  const [hydratingBrand, setHydratingBrand] = useState('');     // name in-flight

  // EPC class-suggestion state (manual fallback within the form)
  const [suggestions, setSuggestions] = useState([]);
  const [suggesting, setSuggesting] = useState(false);
  const [suggestError, setSuggestError] = useState('');
  const [manualClass, setManualClass] = useState('');

  // Parent strength fetch (manual fallback within the form)
  const [fetchingForms, setFetchingForms] = useState(false);
  const [fetchError, setFetchError] = useState('');

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // ── Resolve: name -> hydrated nested draft ──
  const runResolve = async (name) => {
    setResolving(true);
    setResolveError('');
    try {
      const res = await api.get(
        `/admin/medications/catalog/resolve?name=${encodeURIComponent(name)}`
      );
      const draft = res.data || {};
      setForm(resolveToForm(draft));
      setPickedName(draft.picked_name || name);
      setWasBrandPick(!!draft.is_branded_pick);
      setBrandSuggestions(draft.brand_suggestions || []);
      setSuggestions([]); setSuggestError('');
      setFetchError('');
      setPhase('form');
      if (!draft.generic_name) {
        setResolveError('RxNorm returned nothing usable. Edit the fields manually.');
      }
    } catch {
      setForm({ ...EMPTY_ENTRY, generic_name: name });
      setPickedName(name);
      setWasBrandPick(false);
      setBrandSuggestions([]);
      setPhase('form');
      setResolveError('RxNorm lookup failed. Enter the details manually.');
    } finally {
      setResolving(false);
    }
  };

  const onPickName = (name) => { runResolve(name); };

  const onManual = () => {
    setForm(EMPTY_ENTRY);
    setPickedName('');
    setWasBrandPick(false);
    setBrandSuggestions([]);
    setPhase('form');
  };

  // ── Brand panes ──

  // Click a suggested brand -> hydrate it -> append a pane; drop the suggestion.
  const addBrand = async (brandName) => {
    // Guard against double-add (already present, case-insensitive).
    if (form.brands.some(b => b.brand_name.toLowerCase() === brandName.toLowerCase())) {
      setBrandSuggestions(prev => prev.filter(b => b.toLowerCase() !== brandName.toLowerCase()));
      return;
    }
    setHydratingBrand(brandName);
    try {
      const res = await api.get(
        `/admin/medications/catalog/hydrate-brand?brand=${encodeURIComponent(brandName)}`
      );
      const b = res.data || {};
      const pane = {
        brand_name: b.brand_name || brandName,
        bn_cui: b.bn_cui || '',
        sort_order: form.brands.length,
        forms: (b.forms || []).map(draftFormToRow),
      };
      setForm(f => ({ ...f, brands: [...f.brands, pane] }));
    } catch {
      // Hydration failed — add an empty pane so the curator can hand-enter.
      setForm(f => ({
        ...f,
        brands: [...f.brands, {
          brand_name: brandName, bn_cui: '', sort_order: f.brands.length, forms: [],
        }],
      }));
    } finally {
      setHydratingBrand('');
      setBrandSuggestions(prev =>
        prev.filter(b => b.toLowerCase() !== brandName.toLowerCase()));
    }
  };

  // Add a brand by hand (not from suggestions) — empty pane.
  const addManualBrand = () => {
    setForm(f => ({
      ...f,
      brands: [...f.brands, {
        brand_name: '', bn_cui: '', sort_order: f.brands.length, forms: [],
      }],
    }));
  };

  const updateBrand = (idx, updated) => {
    setForm(f => {
      const brands = [...f.brands];
      brands[idx] = updated;
      return { ...f, brands };
    });
  };

  const removeBrand = (idx) => {
    setForm(f => {
      const removed = f.brands[idx];
      const brands = f.brands.filter((_, i) => i !== idx);
      // Return the removed brand to the suggestion list if it has a name.
      if (removed?.brand_name) {
        setBrandSuggestions(prev =>
          prev.some(b => b.toLowerCase() === removed.brand_name.toLowerCase())
            ? prev : [...prev, removed.brand_name]);
      }
      return { ...f, brands };
    });
  };

  // ── Parent forms ──
  const setParentForm = (idx, key, value) => {
    setForm(f => {
      const forms = [...f.forms];
      forms[idx] = { ...forms[idx], [key]: value };
      return { ...f, forms };
    });
  };
  const toggleParentForm = (idx, checked) => setParentForm(idx, 'include', checked);
  const addParentForm = () => setForm(f => ({
    ...f,
    forms: [...f.forms, { display_label: '', strength_form: '', rxnorm_cui: '', include: true, sort_order: f.forms.length }],
  }));
  const removeParentForm = (idx) => setForm(f => ({
    ...f, forms: f.forms.filter((_, i) => i !== idx),
  }));

  // Manual re-fetch of the parent's generic strengths from RxNorm.
  const fetchParentForms = async () => {
    const name = form.generic_name.trim();
    if (!name) { setFetchError('Enter a generic name first.'); return; }
    setFetchingForms(true);
    setFetchError('');
    try {
      const res = await api.get(
        `/reference/medications/rxnorm?q=${encodeURIComponent(name)}`
      );
      const results = (res.data || []).filter(r => !r.is_branded); // SCD only on parent
      if (results.length === 0) {
        setFetchError('RxNorm returned no generic strengths.');
      }
      // Merge: keep existing, append any not already present (by CUI or label).
      setForm(f => {
        const existing = f.forms;
        const have = new Set(existing.map(x => x.rxnorm_cui || x.display_label));
        const added = results
          .filter(r => !have.has(r.rxnorm_cui || r.display_label))
          .map((r, i) => ({
            display_label: r.display_label,
            strength_form: r.raw_name || r.display_label,
            rxnorm_cui: r.rxnorm_cui || '',
            include: false,
            sort_order: existing.length + i,
          }));
        return { ...f, forms: [...existing, ...added] };
      });
    } catch {
      setFetchError('RxNorm lookup failed.');
    } finally {
      setFetchingForms(false);
    }
  };

  // ── EPC class suggestions ──
  const fetchSuggestions = useCallback(async (name) => {
    const n = (name || '').trim();
    if (!n) { setSuggestError('Enter a generic name first.'); return; }
    setSuggesting(true);
    setSuggestError('');
    try {
      const res = await api.get(
        `/admin/medications/catalog/suggest-classes?name=${encodeURIComponent(n)}`
      );
      const sugg = res.data || [];
      setSuggestions(sugg);
      if (sugg.length === 0) {
        setSuggestError('No EPC classes found — add manually below if needed.');
      }
    } catch {
      setSuggestError('Class lookup failed. Add manually below if needed.');
    } finally {
      setSuggesting(false);
    }
  }, []);

  const isClassSelected = (cid) => form.classes.some(c => c.class_id === cid);

  const toggleClass = (sugg) => {
    if (isClassSelected(sugg.class_id)) {
      setForm(f => ({ ...f, classes: f.classes.filter(c => c.class_id !== sugg.class_id) }));
    } else {
      setForm(f => ({
        ...f,
        classes: [...f.classes, {
          class_id: sugg.class_id, class_name: sugg.class_name,
          source: 'epc', sort_order: f.classes.length,
        }],
      }));
    }
  };

  const removeClass = (cid) =>
    setForm(f => ({ ...f, classes: f.classes.filter(c => c.class_id !== cid) }));

  const addManualClass = () => {
    const name = manualClass.trim();
    if (!name) return;
    const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
    const cid = `NEPHROS:${slug}`;
    if (form.classes.some(c => c.class_id === cid)) { setManualClass(''); return; }
    setForm(f => ({
      ...f,
      classes: [...f.classes, {
        class_id: cid, class_name: name, source: 'manual', sort_order: f.classes.length,
      }],
    }));
    setManualClass('');
  };

  const save = async () => {
    if (!form.generic_name.trim()) { setError('Generic name is required.'); return; }
    setSaving(true);
    setError('');
    try {
      const payload = formToPayload(form);
      let res;
      if (isEdit) {
        res = await api.put(`/admin/medications/catalog/${entry.id}`, payload);
      } else {
        res = await api.post('/admin/medications/catalog', payload);
      }
      onSaved(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const retire = async () => {
    setSaving(true);
    setError('');
    try {
      const res = await api.put(`/admin/medications/catalog/${entry.id}`, { is_active: false });
      onSaved(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Retire failed.');
    } finally {
      setSaving(false);
    }
  };

  const openSuggestions = suggestions.filter(s => !isClassSelected(s.class_id));
  const hasBrandColumn = phase === 'form' &&
    (form.brands.length > 0 || brandSuggestions.length > 0);

  const title = isEdit
    ? 'Edit Catalog Entry'
    : (phase === 'search' ? 'Add Catalog Entry' : 'Review Catalog Entry');

  return (
    <div style={m.overlay}>
      <div style={{ ...m.modal, width: hasBrandColumn ? '1200px' : '600px' }}>
        <div style={m.header}>
          <h2 style={m.title}>{title}</h2>
          <button onClick={onClose} style={m.closeBtn} aria-label="Close">
            <IconX size={18} stroke={2} />
          </button>
        </div>

        {resolving && (
          <div style={m.resolveOverlay}>
            <IconRefresh size={18} stroke={2} style={{ color: 'var(--color-primary)' }} />
            <span style={m.resolveText}>Resolving from RxNorm…</span>
          </div>
        )}

        {phase === 'search' ? (
          <NameSearch onPick={onPickName} onManual={onManual} />
        ) : (
          <>
            <div style={m.twoCol}>
              {/* ── LEFT: ingredient identity, classes, parent SCD forms ── */}
              <div style={{ ...m.body, flex: hasBrandColumn ? '0 0 640px' : 1 }}>
                {!isEdit && pickedName && (
                  <div style={m.provenance}>
                    <span>
                      {wasBrandPick
                        ? <>Resolved from brand <strong>{pickedName}</strong> → ingredient entry.</>
                        : <>Resolved from <strong>{pickedName}</strong>.</>}
                    </span>
                    <button style={m.linkInline} onClick={() => setPhase('search')}>
                      Pick a different drug
                    </button>
                  </div>
                )}
                {resolveError && <div style={m.warn}>{resolveError}</div>}

                <div style={m.field}>
                  <label style={m.label}>Generic name <span style={m.required}>*</span></label>
                  <input style={m.input} value={form.generic_name}
                    onChange={e => setField('generic_name', e.target.value)}
                    placeholder="e.g. empagliflozin" />
                </div>

                <div style={m.field}>
                  <label style={m.label}>Ingredient RxNorm CUI <span style={m.optional}>(optional)</span></label>
                  <input style={m.input} value={form.rxnorm_cui_ingredient}
                    onChange={e => setField('rxnorm_cui_ingredient', e.target.value)}
                    placeholder="e.g. 1545653" />
                </div>

                <label style={m.checkRow}>
                  <input type="checkbox" checked={form.highlight_in_list}
                    onChange={e => setField('highlight_in_list', e.target.checked)} />
                  <span style={m.checkLabel}>Highlight (teal) in medication list</span>
                </label>
                <label style={m.checkRow}>
                  <input type="checkbox" checked={form.is_active}
                    onChange={e => setField('is_active', e.target.checked)} />
                  <span style={m.checkLabel}>Active</span>
                </label>

                {/* Pharmacologic classes */}
                <div style={m.section}>
                  <div style={m.sectionHead}>
                    <span style={m.label}>Pharmacologic classes</span>
                    <button
                      style={{ ...m.smallBtn, opacity: suggesting ? 0.5 : 1 }}
                      onClick={() => fetchSuggestions(form.generic_name)}
                      disabled={suggesting}
                      title="Look up FDA pharmacologic classes (EPC) from RxClass"
                    >
                      <IconRefresh size={13} stroke={2} />
                      {suggesting ? 'Looking up...' : 'Suggest from RxClass'}
                    </button>
                  </div>

                  {suggestError && <div style={m.hint}>{suggestError}</div>}

                  {form.classes.length > 0 && (
                    <div style={m.chosenWrap}>
                      {form.classes.map(c => (
                        <span key={c.class_id} style={m.classChip}>
                          {c.class_name}
                          {c.source === 'manual' && <span style={m.manualTag}>manual</span>}
                          <button style={m.chipX} onClick={() => removeClass(c.class_id)} aria-label="Remove">
                            <IconX size={11} stroke={2.5} />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}

                  {openSuggestions.length > 0 && (
                    <div style={m.suggestList}>
                      <div style={m.suggestLabel}>Suggested — click to add:</div>
                      {openSuggestions.map(s => (
                        <button key={s.class_id} style={m.suggestRow} onClick={() => toggleClass(s)}>
                          <IconPlus size={12} stroke={2} style={{ color: 'var(--color-primary)' }} />
                          {s.class_name}
                        </button>
                      ))}
                    </div>
                  )}

                  <div style={m.manualRow}>
                    <input
                      style={{ ...m.input, flex: 1 }}
                      value={manualClass}
                      onChange={e => setManualClass(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addManualClass(); } }}
                      placeholder="Add a class manually (e.g. Alkali Therapy)"
                    />
                    <button style={m.smallBtn} onClick={addManualClass}>
                      <IconPlus size={13} stroke={2} /> Add
                    </button>
                  </div>
                </div>

                {/* Parent generic strength forms */}
                <div style={m.section}>
                  <div style={m.sectionHead}>
                    <span style={m.label}>
                      Generic strengths
                      <span style={m.optional}> · check to include</span>
                    </span>
                    <button
                      style={{ ...m.smallBtn, opacity: fetchingForms ? 0.5 : 1 }}
                      onClick={fetchParentForms}
                      disabled={fetchingForms}
                      title="Fetch generic strengths from RxNorm for this ingredient"
                    >
                      <IconRefresh size={13} stroke={2} />
                      {fetchingForms ? 'Fetching...' : 'Fetch from RxNorm'}
                    </button>
                  </div>
                  {fetchError && <div style={m.hint}>{fetchError}</div>}
                  <FormRowList
                    rows={form.forms}
                    onToggle={toggleParentForm}
                    onEdit={setParentForm}
                    onRemove={removeParentForm}
                    onAddManual={addParentForm}
                    emptyHint="No generic strengths yet — fetch from RxNorm above, or add manually."
                  />
                </div>

                {error && <div style={m.error}>{error}</div>}
              </div>

              {/* ── RIGHT: brand panes + suggestions ── */}
              {hasBrandColumn && (
                <div style={m.brandCol}>
                  <div style={m.brandColHead}>Brands</div>

                  {/* Click-to-add suggestions */}
                  {brandSuggestions.length > 0 && (
                    <div style={m.brandSuggestRow}>
                      <span style={m.brandSuggestLabel}>Add a brand:</span>
                      {brandSuggestions.map(b => (
                        <button key={b} style={m.brandSuggestChip}
                          onClick={() => addBrand(b)}
                          disabled={!!hydratingBrand}
                          title={`Add ${b}`}>
                          {hydratingBrand === b
                            ? <IconRefresh size={11} stroke={2.5} style={{ color: 'var(--color-primary)' }} />
                            : <IconPlus size={11} stroke={2.5} style={{ color: 'var(--color-primary)' }} />}
                          {b}
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Hydrated brand panes */}
                  {form.brands.map((b, idx) => (
                    <BrandPane
                      key={`${b.brand_name}-${idx}`}
                      brand={b}
                      onChange={(updated) => updateBrand(idx, updated)}
                      onRemove={() => removeBrand(idx)}
                    />
                  ))}

                  <button style={m.linkBtn} onClick={addManualBrand}>
                    + add a brand manually
                  </button>
                </div>
              )}
            </div>

            <div style={m.footer}>
              <div>
                {isEdit && form.is_active && (
                  <Button variant="caution" size="sm" onClick={retire} disabled={saving}>Retire</Button>
                )}
                {/* When there are no suggestions and no brands yet, still allow
                    adding a brand by hand from the left-only layout. */}
                {!hasBrandColumn && (
                  <Button variant="ghost" size="sm" onClick={addManualBrand} disabled={saving}>
                    + Add brand
                  </Button>
                )}
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <Button variant="secondary" size="sm" onClick={onClose} disabled={saving}>Cancel</Button>
                <Button variant="primary" size="sm" onClick={save} disabled={saving}>
                  {saving ? 'Saving...' : 'Save'}
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ===========================================================================
// MedicationsCatalogTab
// ===========================================================================

export default function MedicationsCatalogTab() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [editing, setEditing] = useState(undefined); // undefined=closed, null=add, obj=edit

  const [refreshing, setRefreshing] = useState(false);
  const [refreshMsg, setRefreshMsg] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/medications/catalog?include_inactive=${includeInactive}`);
      setEntries(res.data || []);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load catalog.');
    } finally {
      setLoading(false);
    }
  }, [includeInactive]);

  useEffect(() => { load(); }, [load]);

  const refreshIndex = async () => {
    setRefreshing(true);
    setRefreshMsg('');
    try {
      const res = await api.post('/admin/medications/refresh-drug-index');
      const d = res.data || {};
      if (d.status === 'ok') {
        setRefreshMsg(`Drug name index updated — ${d.count.toLocaleString()} terms.`);
      } else {
        setRefreshMsg(`Refresh failed (${d.status}). The existing index is unchanged.`);
      }
    } catch (e) {
      setRefreshMsg(e?.response?.data?.detail || 'Refresh failed.');
    } finally {
      setRefreshing(false);
    }
  };

  const onSaved = () => { setEditing(undefined); load(); };

  const filtered = entries.filter(e => {
    if (!query.trim()) return true;
    const q = query.trim().toLowerCase();
    return e.generic_name.toLowerCase().includes(q)
      || (e.brands || []).some(b => b.brand_name.toLowerCase().includes(q));
  });

  return (
    <div>
      <div style={p.headerRow}>
        <h2 style={p.sectionTitle}>Medications Catalog</h2>
        <p style={p.pageSub}>Curated nephrology drug list. Drives search, classification, and care optimization.</p>
      </div>

      <div style={p.controls}>
        <div style={p.searchBar}>
          <IconSearch size={16} stroke={1.5} style={{ color: 'var(--color-text-muted)' }} />
          <input style={p.searchInput} value={query}
            onChange={e => setQuery(e.target.value)} placeholder="Filter by name or brand..." />
        </div>
        <label style={p.checkRow}>
          <input type="checkbox" checked={includeInactive}
            onChange={e => setIncludeInactive(e.target.checked)} />
          <span style={p.checkLabel}>Show retired</span>
        </label>

        <div style={{ flex: 1 }} />

        <button
          style={{ ...p.refreshBtn, opacity: refreshing ? 0.5 : 1 }}
          onClick={refreshIndex}
          disabled={refreshing}
          title="Refresh the drug-name index used for RxNorm type-ahead search. The index also self-refreshes monthly."
        >
          <IconRefresh size={15} stroke={1.5} />
          {refreshing ? 'Refreshing...' : 'Refresh Index'}
        </button>
        <Button variant="primary" size="sm" onClick={() => setEditing(null)}>
          + Add Entry
        </Button>
      </div>

      {refreshMsg && <div style={p.refreshMsg}>{refreshMsg}</div>}

      {loading ? (
        <div style={p.empty}>Loading...</div>
      ) : error ? (
        <div style={{ ...p.empty, color: 'var(--color-severity-high)' }}>{error}</div>
      ) : filtered.length === 0 ? (
        <div style={p.empty}>
          {entries.length === 0
            ? 'The catalog is empty. Click “Add Entry” to search RxNorm and build it.'
            : 'No entries match your filter.'}
        </div>
      ) : (
        <div style={p.tableWrapper}>
          <table style={p.table}>
            <thead>
              <tr>
                <th style={p.th}>Generic name</th>
                <th style={p.th}>Brands</th>
                <th style={p.th}>Classes</th>
                <th style={{ ...p.th, width: '70px', textAlign: 'center' }}>Strengths</th>
                <th style={{ ...p.th, width: '70px', textAlign: 'center' }}>Highlight</th>
                <th style={{ ...p.th, width: '44px' }} />
              </tr>
            </thead>
            <tbody>
              {filtered.map(e => {
                // Total strengths = parent generic forms + all brand forms.
                const strengthCount = (e.forms?.length || 0)
                  + (e.brands || []).reduce((n, b) => n + (b.forms?.length || 0), 0);
                return (
                  <tr key={e.id} style={{ ...p.tr, opacity: e.is_active ? 1 : 0.5 }}>
                    <td style={p.td}>
                      <span style={{ fontWeight: 600, color: e.highlight_in_list ? 'var(--color-primary)' : 'var(--color-text-primary)' }}>
                        {e.generic_name}
                      </span>
                      {!e.is_active && <span style={p.retiredTag}>retired</span>}
                    </td>
                    <td style={{ ...p.td, color: 'var(--color-text-muted)' }}>
                      {(e.brands || []).map(b => b.brand_name).join(', ') || '—'}
                    </td>
                    <td style={p.td}>
                      {(e.classes && e.classes.length) ? (
                        <span style={p.chipWrap}>
                          {e.classes.map(c => (
                            <span key={c.class_id} style={p.classChip}>{c.class_name}</span>
                          ))}
                        </span>
                      ) : <span style={{ color: 'var(--color-text-muted)' }}>—</span>}
                    </td>
                    <td style={p.tdCenter}>{strengthCount}</td>
                    <td style={p.tdCenter}>{e.highlight_in_list ? '●' : ''}</td>
                    <td style={p.tdCenter}>
                      <button style={p.iconBtn} onClick={() => setEditing(e)} aria-label="Edit">
                        <IconPencil size={16} stroke={1.5} style={{ color: 'var(--color-text-muted)' }} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {editing !== undefined && (
        <CatalogEntryModal
          entry={editing}
          onSaved={onSaved}
          onClose={() => setEditing(undefined)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const p = {
  headerRow: { marginBottom: '16px' },
  sectionTitle: { fontSize: 'var(--font-size-lg)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-primary)', margin: 0, fontFamily: 'var(--font-family-base)' },
  pageSub: { fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)', margin: '4px 0 0', fontFamily: 'var(--font-family-base)' },
  controls: { display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '12px' },
  searchBar: { display: 'flex', alignItems: 'center', gap: '8px', flex: '0 1 320px', maxWidth: '320px', padding: '7px 12px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)', backgroundColor: 'var(--color-bg-card)' },
  searchInput: { flex: 1, border: 'none', outline: 'none', fontSize: '13px', fontFamily: 'var(--font-family-base)', color: 'var(--color-text-primary)', backgroundColor: 'transparent' },
  checkRow: { display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', flexShrink: 0 },
  checkLabel: { fontSize: '13px', color: 'var(--color-text-secondary)', fontFamily: 'var(--font-family-base)' },
  refreshBtn: { display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '13px', fontWeight: 500, color: 'var(--color-text-secondary)', background: 'var(--color-bg-card)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)', padding: '6px 12px', cursor: 'pointer', fontFamily: 'var(--font-family-base)', flexShrink: 0 },
  refreshMsg: { fontSize: '12px', color: 'var(--color-text-muted)', fontFamily: 'var(--font-family-base)', margin: '-4px 0 12px' },
  empty: { padding: '48px 24px', textAlign: 'center', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)', fontStyle: 'italic', fontFamily: 'var(--font-family-base)' },
  tableWrapper: { border: '1px solid var(--color-border)', borderTop: '3px solid var(--color-accent-navy)', borderRadius: '0 0 var(--radius-sm) var(--radius-sm)', backgroundColor: 'var(--color-bg-card)' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 'var(--font-size-table)', fontFamily: 'var(--font-family-base)' },
  th: { textAlign: 'left', padding: '8px 12px', fontSize: 'var(--font-size-xs)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-muted)', borderBottom: '1px solid var(--color-border)', backgroundColor: 'var(--color-subNav)', textTransform: 'uppercase', letterSpacing: '0.04em' },
  tr: { borderBottom: '1px solid var(--color-border)' },
  td: { padding: '9px 12px', color: 'var(--color-text-primary)', verticalAlign: 'middle' },
  tdCenter: { padding: '9px 8px', textAlign: 'center', verticalAlign: 'middle' },
  iconBtn: { background: 'none', border: 'none', cursor: 'pointer', padding: '2px', display: 'inline-flex', alignItems: 'center' },
  chipWrap: { display: 'flex', flexWrap: 'wrap', gap: '4px' },
  classChip: { fontSize: '11px', fontWeight: 600, color: 'var(--color-primary)', backgroundColor: 'var(--color-accent-wash)', padding: '2px 7px', borderRadius: '4px', whiteSpace: 'nowrap' },
  retiredTag: { fontSize: '10px', color: 'var(--color-text-muted)', marginLeft: '8px', fontStyle: 'italic' },
};

const m = {
  overlay: { position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200 },
  modal: { position: 'relative', backgroundColor: 'var(--color-bg-card)', borderRadius: 'var(--radius-lg)', maxWidth: '95vw', maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: 'var(--shadow-lg)', transition: 'width 0.2s ease' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px 14px', borderBottom: '1px solid var(--color-border)', flexShrink: 0 },
  title: { fontSize: '17px', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-primary)', margin: 0, fontFamily: 'var(--font-family-base)' },
  closeBtn: { background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', padding: '4px', display: 'flex' },
  resolveOverlay: { position: 'absolute', inset: 0, top: '57px', backgroundColor: 'rgba(255,255,255,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', zIndex: 5, borderRadius: 'var(--radius-lg)' },
  resolveText: { fontSize: '13px', fontWeight: 600, color: 'var(--color-primary)', fontFamily: 'var(--font-family-base)' },

  twoCol: { display: 'flex', flex: 1, overflow: 'hidden', minHeight: 0 },
  body: { padding: '16px 24px', display: 'flex', flexDirection: 'column', gap: '12px', overflowY: 'auto' },
  brandCol: { flex: 1, borderLeft: '1px solid var(--color-border)', backgroundColor: 'var(--color-bg-subtle)', padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: '12px', overflowY: 'auto' },
  brandColHead: { fontSize: '12px', fontWeight: 600, color: 'var(--color-text-primary)', textTransform: 'uppercase', letterSpacing: '0.04em', fontFamily: 'var(--font-family-base)' },

  provenance: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', fontSize: '12px', color: 'var(--color-text-secondary)', fontFamily: 'var(--font-family-base)', backgroundColor: 'var(--color-accent-wash)', padding: '8px 10px', borderRadius: 'var(--radius-sm)' },
  warn: { fontSize: '12px', color: 'var(--color-severity-mod)', fontFamily: 'var(--font-family-base)', backgroundColor: 'var(--color-severity-mod-bg)', padding: '8px 10px', borderRadius: 'var(--radius-sm)' },
  linkInline: { fontSize: '12px', color: 'var(--color-primary)', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'var(--font-family-base)', textDecoration: 'underline', flexShrink: 0, padding: 0 },
  field: { display: 'flex', flexDirection: 'column', gap: '4px' },
  label: { fontSize: '12px', fontWeight: 600, color: 'var(--color-text-primary)', fontFamily: 'var(--font-family-base)' },
  required: { color: 'var(--color-severity-high)' },
  optional: { color: 'var(--color-text-muted)', fontWeight: 400, fontSize: '11px' },
  input: { fontSize: '13px', padding: '7px 10px', border: '1px solid var(--color-border)', borderRadius: '4px', fontFamily: 'var(--font-family-base)', color: 'var(--color-text-primary)', backgroundColor: 'var(--color-bg-card)', outline: 'none', width: '100%', boxSizing: 'border-box' },
  checkRow: { display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' },
  checkLabel: { fontSize: '12px', color: 'var(--color-text-primary)', fontFamily: 'var(--font-family-base)' },
  section: { borderTop: '1px solid var(--color-border)', paddingTop: '12px', display: 'flex', flexDirection: 'column', gap: '8px' },
  sectionHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  smallBtn: { display: 'inline-flex', alignItems: 'center', gap: '3px', fontSize: '11px', color: 'var(--color-primary)', background: 'none', border: '1px solid var(--color-accent-tint)', borderRadius: '4px', padding: '3px 8px', cursor: 'pointer', fontFamily: 'var(--font-family-base)', fontWeight: 600 },
  hint: { fontSize: '11px', color: 'var(--color-text-muted)', fontFamily: 'var(--font-family-base)' },
  chosenWrap: { display: 'flex', flexWrap: 'wrap', gap: '6px' },
  classChip: { display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '11px', fontWeight: 600, color: 'var(--color-primary)', backgroundColor: 'var(--color-accent-wash)', padding: '3px 6px 3px 8px', borderRadius: '4px' },
  manualTag: { fontSize: '9px', fontWeight: 400, color: 'var(--color-text-muted)', fontStyle: 'italic', marginLeft: '2px' },
  chipX: { background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-primary)', padding: 0, display: 'inline-flex', alignItems: 'center' },
  suggestList: { display: 'flex', flexDirection: 'column', gap: '2px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)', padding: '8px', backgroundColor: 'var(--color-bg-subtle)' },
  suggestLabel: { fontSize: '11px', fontWeight: 600, color: 'var(--color-text-muted)', marginBottom: '4px', fontFamily: 'var(--font-family-base)' },
  suggestRow: { display: 'flex', alignItems: 'center', gap: '6px', padding: '4px 6px', cursor: 'pointer', fontSize: '12px', fontFamily: 'var(--font-family-base)', color: 'var(--color-text-primary)', background: 'none', border: 'none', textAlign: 'left', borderRadius: '3px' },
  manualRow: { display: 'flex', gap: '8px', alignItems: 'center' },
  formRow: { display: 'flex', gap: '8px', alignItems: 'center' },
  removeBtn: { background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-severity-high)', padding: '4px', display: 'flex', flexShrink: 0 },
  linkBtn: { fontSize: '11px', color: 'var(--color-primary)', background: 'none', border: 'none', cursor: 'pointer', padding: '2px 0', fontFamily: 'var(--font-family-base)', textDecoration: 'underline', alignSelf: 'flex-start' },
  error: { fontSize: '12px', color: 'var(--color-severity-high)', fontFamily: 'var(--font-family-base)' },
  footer: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 24px', borderTop: '1px solid var(--color-border)', flexShrink: 0 },

  // Brand pane
  brandPane: { border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)', backgroundColor: 'var(--color-bg-card)', padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: '6px' },
  brandPaneHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' },
  brandPaneName: { fontSize: '13px', fontWeight: 600, color: 'var(--color-primary)', fontFamily: 'var(--font-family-base)' },
  brandPaneCui: { fontSize: '10px', color: 'var(--color-text-muted)', fontFamily: 'var(--font-family-base)' },
  brandPaneRemove: { background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', padding: '2px', display: 'flex', flexShrink: 0 },
  brandPaneSub: { fontSize: '10px', color: 'var(--color-text-muted)', fontFamily: 'var(--font-family-base)' },

  // Brand suggestions
  brandSuggestRow: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px' },
  brandSuggestLabel: { fontSize: '11px', color: 'var(--color-text-muted)', fontFamily: 'var(--font-family-base)', width: '100%' },
  brandSuggestChip: { display: 'inline-flex', alignItems: 'center', gap: '3px', fontSize: '11px', fontWeight: 600, color: 'var(--color-primary)', backgroundColor: 'var(--color-bg-card)', border: '1px solid var(--color-accent-tint)', borderRadius: '4px', padding: '3px 8px', cursor: 'pointer', fontFamily: 'var(--font-family-base)' },
};

// NameSearch styles
const ns = {
  wrap: { display: 'flex', flexDirection: 'column', minHeight: '320px', width: '600px', maxWidth: '95vw' },
  searchBar: { display: 'flex', alignItems: 'center', gap: '8px', margin: '16px 24px 0', padding: '8px 12px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)', backgroundColor: 'var(--color-bg-card)' },
  input: { flex: 1, border: 'none', outline: 'none', fontSize: '14px', fontFamily: 'var(--font-family-base)', color: 'var(--color-text-primary)', backgroundColor: 'transparent' },
  searchingText: { fontSize: '11px', color: 'var(--color-text-muted)', fontFamily: 'var(--font-family-base)', flexShrink: 0 },
  results: { flex: 1, overflowY: 'auto', padding: '8px 24px', minHeight: '180px' },
  row: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', padding: '8px 10px', borderRadius: 'var(--radius-sm)', cursor: 'pointer' },
  rowText: { fontSize: '13px', fontWeight: 600, fontFamily: 'var(--font-family-base)', color: 'var(--color-text-primary)' },
  hint: { fontSize: '13px', color: 'var(--color-text-muted)', fontFamily: 'var(--font-family-base)', fontStyle: 'italic', padding: '16px 10px', textAlign: 'center', lineHeight: 1.5 },
  manualRow: { padding: '10px 24px 16px', borderTop: '1px solid var(--color-border)' },
  manualLink: { fontSize: '12px', color: 'var(--color-primary)', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'var(--font-family-base)', textDecoration: 'underline', padding: 0 },
};